import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/styles';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import translationService from '../services/translator.service';
import { userTypeEnum } from '../enums/auth/userType.enum';
import { userPermissionsEnum } from '../enums/auth/userPermissions.enum';
import BottomEdgeDivider from '../components/shared/common/BottomEdgeDivider';
import BottomNavFavoriteIcon from '../components/shared/common/icons/BottomNavFavoriteIcon';
import BottomNavChargePointsIcon from '../components/shared/common/icons/BottomNavChargePointsIcon';
import BottomNavScanIcon from '../components/shared/common/icons/BottomNavScanIcon';
import BottomNavBillingIcon from '../components/shared/common/icons/BottomNavBillingIcon';
import BottomNavDetailsIcon from '../components/shared/common/icons/BottomNavDetailsIcon';

// Defined outside the parent component so React sees a stable component type on every
// render and never unmounts/remounts the navigation items unnecessarily.
const NavigationLinks = ({ value, setValue, translatedData, iconOptions, classes, getIconProps }) => {
    return (
        <BottomNavigation
            value={value}
            onChange={(event, newValue) => {
                window.dispatchEvent(new CustomEvent('mobile-user-menu-close'));
                setValue(newValue);
            }}
            showLabels
            className={classes.root}>
            <BottomNavigationAction
                key={'0'}
                label={translationService.getTranslation(translatedData, 'favorite')}
                component={Link}
                to='/favChargers'
                classes={{ root: classes.navAction, selected: classes.navActionSelected }}
                icon={<BottomNavFavoriteIcon {...getIconProps(0)} />}
            />
            <BottomNavigationAction
                key={'1'}
                label={translationService.getTranslation(translatedData, 'general.titles.chargersTitle')}
                component={Link}
                to='/ChargePointStation'
                classes={{ root: classes.navAction, selected: classes.navActionSelected }}
                icon={<BottomNavChargePointsIcon {...getIconProps(1)} />}
            />
            <BottomNavigationAction
                key={'2'}
                label={translationService.getTranslation(translatedData, 'scan')}
                component={Link}
                to='/QR'
                classes={{ root: classes.navAction, selected: classes.navActionSelected }}
                icon={<BottomNavScanIcon {...getIconProps(2)} />}
            />
            <BottomNavigationAction
                key={'3'}
                label={translationService.getTranslation(translatedData, 'debiting')}
                component={Link}
                to='/payments'
                classes={{ root: classes.navAction, selected: classes.navActionSelected }}
                icon={<BottomNavBillingIcon {...getIconProps(3)} />}
            />
            <BottomNavigationAction
                key={'4'}
                label={translationService.getTranslation(translatedData, 'details')}
                component={Link}
                to='/personal'
                classes={{ root: classes.navAction, selected: classes.navActionSelected }}
                icon={<BottomNavDetailsIcon {...getIconProps(4)} />}
            />
        </BottomNavigation>
    );
};

export const BottomNavigationComponent = ({ userInfoReducer, screenItem, languageReducer, renderShellOnly = false, ...props }) => {
    const { userInfo } = useSelector((state) => state.userInfoReducer);
    const theme = useTheme();
    const classes = useStyles();
    const iconOptions = {
        size: 22,
        activeColor: '#005496',
        inactiveColor: theme.palette.text.primary,
    };
    const location = useLocation();

    const getValueBasedOnRoute = () => {
        if (location.pathname === '/favChargers') {
            return 0;
        }
        if (location.pathname === '/ChargePointStation') {
            return 1;
        }
        if (location.pathname === '/QR') {
            return 2;
        }
        if (location.pathname === '/payments') {
            return 3;
        }
        if (location.pathname === '/personal') {
            return 4;
        }
    };
    const [value, setValue] = useState(getValueBasedOnRoute());
    const [translatedData, setTranslatedData] = useState();
    const shouldRenderShellOnly = renderShellOnly || !userInfoReducer.isLoggedIn;

    useEffect(() => {
        setValue(getValueBasedOnRoute());
        setTranslatedData(translationService.getTranslatedData(languageReducer.language, 'bottomNavigationComponent'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenItem, languageReducer.language]);

    useEffect(() => {
        setValue(getValueBasedOnRoute());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const getIconColor = (itemValue) => (value === itemValue ? iconOptions.activeColor : iconOptions.inactiveColor);

    const getIconProps = (itemValue, overrides = {}) => {
        const iconColor = getIconColor(itemValue);

        return {
            size: iconOptions.size,
            primaryColor: iconColor,
            secondaryColor: iconColor,
            textColor: iconColor,
            ...overrides,
        };
    };

    // Hide this bottom navigation bar when is operation manager with view permmisions only
    if (userInfo && userInfo.userType === userTypeEnum.OperationManager && userInfo.permissions === userPermissionsEnum.View) return null;
    return (
        <div className={classes.navigationShell}>
            <BottomEdgeDivider insetX={16} thickness={1} color='#000000' edge='top' />
            {shouldRenderShellOnly ? (
                <BottomNavigation
                    value={value}
                    onChange={(event, newValue) => {
                        setValue(newValue);
                    }}
                    showLabels
                    className={classes.root}></BottomNavigation>
            ) : (
                <NavigationLinks
                    value={value}
                    setValue={setValue}
                    translatedData={translatedData}
                    iconOptions={iconOptions}
                    classes={classes}
                    getIconProps={getIconProps}
                />
            )}
        </div>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.default,
        width: '100%',
    },
    navigationShell: {
        boxSizing: 'border-box',
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: 8,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)',
        zIndex: 1100,
    },
    stickToBottom: {
        width: '100%',
        position: 'fixed',
        bottom: 0,
    },
    flexCenter: {
        display: 'flex',
        alignItems: 'center',
    },
    navAction: {
        color: theme.palette.text.primary,
    },
    navActionSelected: {
        color: '#005496 !important',
    },
}));

BottomNavigationComponent.propTypes = {
    userInfoReducer: PropTypes.object.isRequired,
    renderShellOnly: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    userInfoReducer: state.userInfoReducer,
    languageReducer: state.languageReducer,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(BottomNavigationComponent);
